import { CouponBase } from "./coupon-base";
import { FirstMatchGroupItemTriggerItemsQualifier } from "../coupon-qualifiers/trigger-items/first-match-group-item-trigger-items";
import { Util } from "../../../util/util";
export class FixedAmountCoupon extends CouponBase {
    _discountAmount;
    constructor(appType, sourceData, dependencies, baseData, identity) {
        super(appType, sourceData, dependencies, baseData, identity);
        this._discountAmount = baseData.discount;
        this._preQualifiers.push(new FirstMatchGroupItemTriggerItemsQualifier(this._data, (candidates, cart) => this._reachesPricePoint(cart, candidates)));
    }
    /**
     * Whether the items matched for one qualifying-items row are actually worth discounting.
     *
     * Bug 75837 (recurrence of 43598): a fixed-price coupon lists every item it is good for
     * (e.g. "Large BYO or Specialty for $20") and some of those items cost less than the price point.
     *
     * Priced through the coupon's own discount pricing, so a candidate is judged by exactly what
     * applying it would yield, including the half & half price-point base and its chargeable extras.
     *
     * Only a single-group coupon can be judged this way. The price point is one whole-coupon figure, so
     * it is only meaningful against the coupon's COMPLETE qualified set; with one group a matched row
     * is that set. Across several groups each group holds just a part of it. A "Large Pizza + 2-Liter
     * for $20" would weigh the 2-liter alone against $20 and hand back a group that qualified perfectly
     * well, failing the coupon outright. Multi-group coupons keep the plain first-match behavior.
     */
    _reachesPricePoint(cart, candidates) {
        if (new Set(this._coupon.items.map(x => x.groupIndex)).size > 1)
            return true;
        const discountItems = this._previewDiscountItems(cart, candidates);
        if (discountItems.length === 0)
            return false;
        const totalAmounts = this._getTotalCouponItemsPrices(discountItems);
        const iPrice = this._getPricePointBase(totalAmounts, discountItems);
        return Util.Float2(iPrice - this._discountAmount - totalAmounts.couponAddOnPrice) > 0;
    }
    meetsPreConditions(cart) {
        return super.meetsPreConditions(cart);
    }
    /**
     * The price the coupon's price point is enforced against.
     *
     * When every qualified item is a half, the price point applies to the split item as a whole, so
     * the base is what the customer is actually being charged for those halves rather than the sum of
     * the halves' covered-price shares. Summing the shares yields the average of the halves' covered
     * prices (AC #82541 G2), which for a split item can sit either side of the Setting 22 price and
     * would leave the customer paying something other than the price point.
     *
     * The charged price includes every topping and every upcharge, so anything the price point does
     * not buy comes back off the base: the toppings a portion carries beyond its own Toppings Covered,
     * and modifiers that add to the price without being priced modifiers such as an upcharged
     * crust (Bug 82910). The price point covers the base item at its size, not the extras. Without
     * this they would fall inside the discount and ride along free.
     *
     * Otherwise the covered price stands, capped at what the qualified items actually cost; a covered
     * price only ever includes covered toppings, so it needs no such adjustment. Note the host apps
     * gate that cap on the coupon's Exact Match flag; this library caps unconditionally, deliberately,
     * so the coupon can never discount more than the items are worth.
     */
    _getPricePointBase(totalAmounts, discountItems) {
        const allQualifiedItemsAreHalves = discountItems.length > 0
            && discountItems.every(item => (item.halfCount ?? 1) > 1);
        if (!allQualifiedItemsAreHalves)
            return Math.min(totalAmounts.couponItemPrice, totalAmounts.orderItemPrice);
        const chargeableExtras = discountItems.reduce((total, item) => total
            + Util.Float2(item.uncoveredModifierPrice ?? 0)
            + Util.Float2(item.upchargeModifierPrice ?? 0), 0);
        return Util.Float2(totalAmounts.orderItemPrice - chargeableExtras);
    }
    getDiscountItems(cart) {
        return this._getDiscountItemsFromTriggers(cart);
    }
    calculateDiscount(cart) {
        const discountItems = this.getDiscountItems(cart);
        const totalAmounts = this._getTotalCouponItemsPrices(discountItems);
        const iPrice = this._getPricePointBase(totalAmounts, discountItems);
        let discountAmount = iPrice - this._discountAmount - totalAmounts.couponAddOnPrice;
        discountAmount = Math.max(0, this._adjustDiscountAmount(cart, discountAmount));
        if (this._coupon.isBeforeTax) {
            this.getBeforeTaxDistributor().distribute({
                cart,
                discountItems,
                totalDiscountedItemsPrice: totalAmounts.orderItemPrice,
                computedDiscountToAllocate: discountAmount,
            });
        }
        else {
            this.getAfterTaxDistributor().distribute({
                cart,
                discountItems,
                totalDiscountedItemsPrice: totalAmounts.orderItemPrice,
                computedDiscountToAllocate: discountAmount
            });
        }
        return [discountAmount, discountItems];
    }
}
//# sourceMappingURL=fixed-amount.js.map